import { request } from "../../api";
import { unwrapList } from "../devices";

export type GroupPolicyDefaultAction = "allow" | "block";
export type PolicyActivation = "always" | "schedule" | "presence";
export type UnknownDomainAction = "log" | "block" | "request_review";

export interface GroupPolicyRow {
  id: string;
  name: string;
  device_group: string;
  device_group_name: string;
  device_count: number;
  url_group_ids: string[];
  url_group_names: string[];
  destination_count: number;
  // M2M to `AppGroup` — drives the compiled policy's `allowed_apps[*]`.
  // Mobile passes each entry to `VpnService.Builder.addDisallowedApplication`
  // so its traffic skips the tunnel. Wire shape spec:
  // docs/changes/2026-05-25-policy-allowed-apps.md
  allowed_app_group_ids: string[];
  allowed_app_group_names: string[];
  allowed_apps_count: number;
  activation: PolicyActivation;
  schedule_profile_id?: string | null;
  schedule_profile_name?: string | null;
  default_action: GroupPolicyDefaultAction;
  unknown_domain_action: UnknownDomainAction;
  network_violation_reporting_enabled: boolean;
  notifications_enabled: boolean;
  active_notification_title: string;
  active_notification_body: string;
  inactive_notification_title: string;
  inactive_notification_body: string;
  priority: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface GroupPolicyCreateInput {
  name: string;
  device_group: string;
  url_groups: string[];
  /** Optional; omit to leave the M2M untouched on PATCH. Send `[]` to clear. */
  allowed_app_groups?: string[];
  default_action: GroupPolicyDefaultAction;
  activation?: PolicyActivation;
  unknown_domain_action?: UnknownDomainAction;
  network_violation_reporting_enabled?: boolean;
  notifications_enabled?: boolean;
  active_notification_title?: string;
  active_notification_body?: string;
  inactive_notification_title?: string;
  inactive_notification_body?: string;
  priority: number;
  valid_from?: string | null;
  valid_until?: string | null;
  is_active?: boolean;
  schedule_profile_id?: string | null;
}

interface Paginated<T> {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: T[];
}

export const groupPoliciesApi = {
  list: async () => {
    const data = await request<Paginated<GroupPolicyRow> | GroupPolicyRow[]>(
      `/api/admin/v1/group-policies/`,
    );
    return unwrapList(data);
  },
  create: (input: GroupPolicyCreateInput) =>
    request<GroupPolicyRow>(`/api/admin/v1/group-policies/`, {
      method: "POST",
      json: input,
    }),
  patch: (id: string, body: Partial<GroupPolicyCreateInput>) =>
    request<GroupPolicyRow>(`/api/admin/v1/group-policies/${id}/`, {
      method: "PATCH",
      json: body,
    }),
  delete: (id: string) =>
    request<undefined>(`/api/admin/v1/group-policies/${id}/`, { method: "DELETE" }),
};
